import { useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BookOpen, Menu, X } from "lucide-react";

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const close = () => setOpen(false);

  const goToHash = useCallback((hash) => (e) => {
    if (e) e.preventDefault();
    close();
    if (location.pathname !== "/") {
      // Navigate to home with the hash — HomePage useEffect handles the scroll
      // after articles finish loading (retry loop).
      navigate("/" + hash);
    } else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.pathname, navigate]);

  const navItems = [
    { to: "/", label: "Home", testid: "nav-home", type: "route" },
    { to: "/why-we-built-summarymaps", label: "Our Story", testid: "nav-our-story", type: "route" },
    { hash: "#articles", label: "Articles", testid: "nav-articles", type: "hash" },
    { hash: "#how", label: "How it works", testid: "nav-how", type: "hash" },
    { to: "/archive", label: "Archive", testid: "nav-archive", type: "route" },
    { hash: "#newsletter", label: "Newsletter", testid: "nav-newsletter", type: "hash" },
    { to: "/about", label: "About", testid: "nav-about", type: "route" },
    { to: "/contact", label: "Contact", testid: "nav-contact", type: "route" },
    { to: "/privacy", label: "Privacy Policy", testid: "nav-privacy", type: "route" },
  ];

  const renderNav = (className) => navItems.map((item) =>
    item.type === "route" ? (
      <Link key={item.label} to={item.to} onClick={close} className={className} data-testid={item.testid}>
        {item.label}
      </Link>
    ) : (
      <a key={item.label} href={item.hash} onClick={goToHash(item.hash)} className={className} data-testid={item.testid}>
        {item.label}
      </a>
    )
  );

  return (
    <header className="sticky top-0 z-40 bg-cream/85 backdrop-blur-md border-b border-ink/5" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 sm:py-4 flex items-center justify-between">
      <Link
        to="/"
        onClick={(e) => {
          if (location.pathname === "/") {
            e.preventDefault();
            window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
          }
          close();
        }}
        className="flex items-center gap-2.5 sm:gap-3"
        data-testid="logo-link"
      >
          <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-brand text-white flex items-center justify-center shadow-sm">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
          </span>
          <div className="leading-tight">
            <div className="font-serif text-base sm:text-xl font-semibold text-ink">Summary<span className="text-brand">Maps</span></div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-ink-subtle -mt-0.5">Journal</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-6 text-sm font-medium text-ink-muted">
          {renderNav("hover:text-brand transition-colors whitespace-nowrap")}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="https://summarymaps.com"
            target="_blank"
            rel="noreferrer"
            className="hidden lg:inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand text-white text-[13px] xl:text-sm font-medium hover:bg-brand-hover transition-colors shadow-sm whitespace-nowrap"
            data-testid="header-cta-btn"
          >
            Try Summary Maps for Free
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden p-2 rounded-lg border border-ink/10 bg-white/70 text-ink"
            aria-label="Toggle menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="absolute top-full left-3 right-3 mt-2 bg-white border border-ink/10 rounded-2xl shadow-lg p-4 xl:hidden animate-slide-up" data-testid="mobile-menu">
          <nav className="flex flex-col text-base font-medium text-ink">
            {renderNav("px-3 py-2.5 rounded-lg hover:bg-cream")}
          </nav>
          <a
            href="https://summarymaps.com"
            target="_blank"
            rel="noreferrer"
            onClick={close}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 px-5 py-3 rounded-full bg-brand text-white text-sm font-medium"
          >
            Try Summary Maps for Free
          </a>
        </div>
      )}
    </header>
  );
}
